// Updated: 2026-08-13
import type { BrandSearchResponse } from '@mrb91002/shared-types'

import { API_CONFIG } from '../config'

import { PublicApiError } from './errors'

/**
 * Every material carried by one brand (lambda-main `GET /api/brand/{brandName}`).
 *
 * Returns the WHOLE `{parent_materials, standalone_materials}` payload in one shot — there is no
 * cursor and no second page — which is why the query layer uses a plain `useQuery` here
 * (react-query-browse-search § D1) and leaves it on the 5-minute baseline `staleTime` (§ D4).
 *
 * `opts.signal` is optional request cancellation forwarded from a React Query `queryFn`, spread
 * conditionally so a caller that omits it makes a byte-identical request (Req 6.1, 6.2). The failure
 * message is unchanged verbatim — `BrandPage` renders it — but now carries the HTTP status so the
 * shared retry predicate never retries a 4xx (Req 6.3, 6.4).
 */
export const fetchBrandMaterials = async (
  brandName: string,
  opts?: { signal?: AbortSignal },
): Promise<BrandSearchResponse> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}/brand/${encodeURIComponent(brandName)}`, {
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })

  if (!response.ok) {
    throw new PublicApiError(`Failed to fetch brand materials: ${response.statusText}`, response.status)
  }

  return response.json() as Promise<BrandSearchResponse>
}
